import type { Messages } from "../types";

export const messages: Messages = {
  shared: {
    title: "Zákaznický účet Fielmann",
    tabs: {
      login: "Přihlásit se",
      signup: "Vytvořit účet",
      ariaLabel: "Ověření",
    },
    languageSwitcher: {
      label: "Vybrat jazyk",
    },
    password: {
      show: "Zobrazit heslo",
      hide: "Skrýt heslo",
    },
    header: {
      logoAriaLabel: "Fielmann",
    },
    footer: {
      ariaLabel: "Zápatí",
      customerService: "Zákaznický servis",
      legalNotice: "Informace o provozovateli",
      dataProtection: "Ochrana údajů",
    },
    loading: {
      ariaLabel: "Okamžik prosím...",
      content: "Data se načítají.",
    },
    genericError: "Došlo k chybě. Zkuste to prosím znovu později.",
  },
  login: {
    subtitle: "Přihlaste se pomocí svého e-mailu a hesla.",
    form: {
      emailLabel: "E-mail *",
      passwordLabel: "Heslo *",
      forgotPassword: "Zapomněli jste heslo?",
      submitButton: "Přihlásit se",
      credentialError: "E-mail a heslo se neshodují.",
    },
  },
  signup: {
    subtitle: {
      before: "Využijte ",
      highlight: "výhod",
      after: " a vytvořte si zákaznický účet Fielmann.",
    },
    benefits: {
      popoverHeading: "Vaše výhody:",
      closeLabel: "Zavřít",
      items: {
        orders:
          "Všechny objednávky Fielmann včetně jejich stavu přehledně na jednom místě.",
        visionValues: "Použijte své údaje o zraku při další objednávce.",
        appointments: "Rezervujte a spravujte své termíny.",
        reorders: "Opakované objednávky jednoduše online.",
      },
    },
    form: {
      firstNameLabel: "Jméno *",
      lastNameLabel: "Příjmení *",
      emailLabel: "E-mail *",
      passwordLabel: "Heslo *",
      passwordRepeatLabel: "Opakovat heslo *",
      submitButton: "Vytvořit účet",
      errorTitle: "Účet se nepodařilo vytvořit.",
      errorMessage:
        "Zkontrolujte prosím zadané údaje nebo to zkuste znovu později.",
    },
    consents: {
      requiredError: "Udělte prosím požadovaný souhlas.",
      unavailableError: "Došlo k chybě. Zkuste to prosím znovu později.",
      required: {
        headline: "Souhlas se zákaznickým účtem",
        subline:
          "Abychom vám mohli ve vašem zákaznickém účtu zobrazovat veškerý obsah (např. nákupy nebo termíny), potřebujeme váš souhlas.",
      },
      optional: {
        headline: "Chcete dostávat personalizovanou reklamu?",
        subline:
          "Volba je na vás: Pokud udělíte souhlas, budete dostávat nabídky a informace přizpůsobené vašim zájmům. Bez souhlasu můžete svůj zákaznický účet používat bez personalizované reklamy.",
      },
    },
  },
  emailVerification: {
    subtitle: "Nemůžete se přihlásit?",
    alertTitle: "Nejprve potvrďte svou e-mailovou adresu.",
    alertBody:
      "Nejprve prosím potvrďte svou e-mailovou adresu, abyste mohli aktivovat svůj účet Fielmann.",
    alertSpamHint: "Nemůžete e-mail najít? Zkontrolujte také složku Spam.",
    resendButton: "Znovu odeslat e-mail",
    backToLogin: "Zpět k přihlášení",
  },
  resetPasswordRequest: {
    subtitle: "Zapomněli jste své heslo?",
    body: "Zadejte prosím e-mailovou adresu, kterou jste použili při vytvoření svého účtu. Pošleme vám odkaz, pomocí kterého si můžete snadno resetovat heslo.",
    emailLabel: "E-mail *",
    submitButton: "Vyžádat odkaz",
    backToLogin: "Zpět k přihlášení",
    banner: {
      closeAria: "Zavřít",
      rateLimit: {
        title: "O zaslání e-mailu jste požádali příliš často",
        body: "Zkontrolujte prosím svou e-mailovou schránku a složku Spam, abyste našli e-mail pro resetování hesla.",
        waitHint:
          "Stále nemůžete e-mail najít? Chvíli prosím počkejte, e-mail možná ještě dorazí.",
      },
      error: {
        title: "Odeslání se nezdařilo",
        body: "Zkuste to prosím znovu později.",
      },
    },
  },
  resetPasswordEmail: {
    success: {
      heading: "E-mail pro resetování hesla byl odeslán.",
      body: "Poslali jsme vám e-mail pro resetování hesla. Zkontrolujte prosím svou e-mailovou schránku.",
      spamHint: "Nemůžete e-mail najít? Zkontrolujte také složku Spam.",
    },
    restart: {
      hint: "Pokud e-mail stále nemůžete najít, můžete proces spustit znovu. Možná jste použili jinou e-mailovou adresu.",
      button: "Zkusit znovu",
    },
    error: {
      heading: "Odeslání se nezdařilo",
      body: "Zkuste to prosím znovu později.",
      retryButton: "Zkuste to znovu",
      rateLimitBody:
        "Příliš mnoho pokusů. Počkejte prosím několik minut, než to zkusíte znovu.",
    },
  },
  setPassword: {
    subtitle: "Nyní si můžete nastavit heslo.",
    passwordLabel: "Heslo *",
    passwordRepeatLabel: "Opakovat heslo *",
    submitButton: "Uložit heslo",
    backToLogin: "Přejít k přihlášení",
    successTitle: "Vaše heslo bylo úspěšně uloženo.",
    successRedirectBody: "Nyní budete přesměrováni na naši úvodní stránku.",
    successRedirectLoginHint: "Přihlaste se prosím pomocí svého nového hesla.",
    successWaitHint: "Počkejte chvilku, prosím.",
  },
  resetPasswordError: {
    expired: {
      heading: "Platnost tohoto odkazu vypršela.",
      body: "Odkaz pro resetování vašeho hesla již není platný. Vraťte se na úvodní stránku a na přihlašovací stránce znovu klikněte na „Zapomněli jste heslo?“.",
    },
    used: {
      heading: "Tento odkaz již byl použit.",
      body: "Tento odkaz byl k resetování hesla již použit. Pokud chcete své heslo znovu změnit, vraťte se na úvodní stránku a na přihlašovací stránce klikněte na „Zapomněli jste heslo?“.",
    },
    generic: {
      heading: "Tento odkaz je neplatný.",
      body: "Tento odkaz nebylo možné použít. Vraťte se na úvodní stránku a na přihlašovací stránce znovu klikněte na „Zapomněli jste heslo?“.",
    },
    backToHome: "Zpět na úvodní stránku",
  },
  toasts: {
    missingRequiredFields:
      "Pro pokračování prosím vyplňte všechna chybějící povinná pole.",
  },
  validation: {
    passwordRequired: "Zadejte prosím heslo.",
    passwordRepeatRequired: "Zadejte prosím heslo znovu.",
    firstNameTooLong: "Jméno nesmí být delší než 30 znaků.",
    firstNameRequired: "Zadejte prosím jméno.",
    firstNameInvalidFormat: "Zadejte prosím platné jméno.",
    lastNameTooLong: "Příjmení nesmí být delší než 30 znaků.",
    lastNameRequired: "Zadejte prosím příjmení.",
    lastNameInvalidFormat: "Zadejte prosím platné příjmení.",
    emailTooLong: "E-mailová adresa je příliš dlouhá.",
    emailRequired: "Zadejte prosím e-mailovou adresu.",
    emailInvalidFormat: "Zadejte prosím platnou e-mailovou adresu.",
    passwordMatchesEmail: "Heslo nesmí být stejné jako e-mailová adresa.",
    passwordMismatch: "Hesla se neshodují.",
  },
  passwordRequirements: {
    minimumLength: "Alespoň 10 znaků",
    mixedCase: "Alespoň jedno velké a jedno malé písmeno",
    hasNumber: "Alespoň jedna číslice",
    specialCharactersAllowed: "Speciální znaky jsou povoleny (např. !@#$%^&*)",
  },
};
